import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  MdCheckCircle,
  MdDelete,
  MdWarning,
  MdCameraAlt,
  MdRefresh,
  MdNavigateNext,
  MdNavigateBefore,
} from "react-icons/md";
import Card from "components/card";
import { authService } from "services/authService";
import { faceService, FaceEmbeddingItem } from "services/admin/faceService";
import { useLanguage } from "context/LanguageContext";

const MAX_SLOTS = 3;
const SLOT_LABELS = ["front", "left", "right"];
const SLOT_NAME_KEYS = [
  "faceEnroll.slot.front",
  "faceEnroll.slot.left",
  "faceEnroll.slot.right",
];
const SLOT_INSTRUCTION_KEYS = [
  "faceEnroll.instruction.front",
  "faceEnroll.instruction.left",
  "faceEnroll.instruction.right",
];

type StepStatus =
  | "pending"
  | "capturing"
  | "captured"
  | "uploading"
  | "done"
  | "error";

interface FaceEnrollCardProps {
  userId?: string;
  readonly?: boolean;
  canModifyExisting?: boolean;
  onAllComplete?: () => void;
}

function dataUrlToFile(dataUrl: string, filename: string): File {
  const [header, base64] = dataUrl.split(",", 2);
  if (!header || !base64) {
    throw new Error("faceEnroll.error.processCaptureFailed");
  }
  const mimeMatch = header.match(/data:(.*?);base64/);
  const mime = mimeMatch?.[1] || "image/jpeg";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mime });
}

export default function FaceEnrollCard({
  userId: userIdProp,
  readonly = false,
  canModifyExisting = false,
  onAllComplete,
}: FaceEnrollCardProps) {
  const { t } = useLanguage();
  const [embeddings, setEmbeddings] = useState<FaceEmbeddingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [stepStatuses, setStepStatuses] = useState<StepStatus[]>(
    Array(MAX_SLOTS).fill("pending")
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [captured, setCaptured] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [allComplete, setAllComplete] = useState(false);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const userId = userIdProp ?? authService.getStoredUser()?.id ?? "";

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const data = await faceService.list(userId);
      setEmbeddings(data);

      const newStatuses: StepStatus[] = Array(MAX_SLOTS).fill("pending");
      let firstPending = -1;
      for (let i = 0; i < MAX_SLOTS; i++) {
        if (data.find((e) => e.label === SLOT_LABELS[i])) {
          newStatuses[i] = "done";
        } else if (firstPending === -1) {
          firstPending = i;
        }
      }
      setStepStatuses(newStatuses);

      // Use presigned image URLs from API response
      const urls: Record<string, string> = {};
      for (const e of data) {
        if (SLOT_LABELS.includes(e.label) && e.image_url) {
          urls[e.label] = e.image_url;
        }
      }
      setImageUrls(urls);

      if (firstPending === -1) {
        setAllComplete(true);
        setCurrentStep(0);
      } else {
        setAllComplete(false);
        setCurrentStep(firstPending);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "state.loadFailed");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (allComplete && onAllComplete) {
      onAllComplete();
    }
  }, [allComplete, onAllComplete]);

  // Camera management
  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(
    () => () => {
      stopCamera();
    },
    [stopCamera]
  );

  const startCamera = useCallback(async () => {
    setCameraError(null);
    setCaptured(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 640, height: 480 },
      });
      streamRef.current = stream;
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 100);
    } catch {
      setCameraError("faceEnroll.error.cameraAccess");
    }
  }, []);

  // Auto-start camera when entering a capturing step
  useEffect(() => {
    if (readonly) {
      stopCamera();
      return;
    }
    if (loading || allComplete) return;
    if (
      stepStatuses[currentStep] === "pending" ||
      stepStatuses[currentStep] === "capturing"
    ) {
      setStepStatuses((prev) => {
        const next = [...prev];
        next[currentStep] = "capturing";
        return next;
      });
      stopCamera();
      startCamera();
    }
    return () => {
      // Only stop if navigating away
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, loading, allComplete, readonly, stopCamera]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext("2d")?.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    setCaptured(dataUrl);
    stopCamera();
    setStepStatuses((prev) => {
      const next = [...prev];
      next[currentStep] = "captured";
      return next;
    });
  }, [stopCamera, currentStep]);

  const retakePhoto = useCallback(() => {
    setCaptured(null);
    setError(null);
    setStepStatuses((prev) => {
      const next = [...prev];
      next[currentStep] = "capturing";
      return next;
    });
    startCamera();
  }, [startCamera, currentStep]);

  const confirmAndUpload = useCallback(async () => {
    if (!captured) return;
    setError(null);
    setStepStatuses((prev) => {
      const next = [...prev];
      next[currentStep] = "uploading";
      return next;
    });

    try {
      const file = dataUrlToFile(
        captured,
        `face_${SLOT_LABELS[currentStep]}.jpg`
      );
      await faceService.upload(userId, file, SLOT_LABELS[currentStep]);

      setStepStatuses((prev) => {
        const next = [...prev];
        next[currentStep] = "done";
        return next;
      });
      setCaptured(null);

      // Reload embeddings to get latest
      const data = await faceService.list(userId);
      setEmbeddings(data);

      // Use presigned image URL from re-fetched data
      const uploaded = data.find((e) => e.label === SLOT_LABELS[currentStep]);
      if (uploaded?.image_url) {
        setImageUrls((prev) => ({
          ...prev,
          [SLOT_LABELS[currentStep]]: uploaded.image_url!,
        }));
      }

      // Auto-advance to next pending step
      let nextPending = -1;
      for (let i = 0; i < MAX_SLOTS; i++) {
        if (
          i !== currentStep &&
          !data.find((e) => e.label === SLOT_LABELS[i])
        ) {
          nextPending = i;
          break;
        }
      }

      if (nextPending === -1) {
        setAllComplete(true);
        stopCamera();
      } else {
        setCurrentStep(nextPending);
      }
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : "faceEnroll.error.uploadFailed"
      );
      setStepStatuses((prev) => {
        const next = [...prev];
        next[currentStep] = "error";
        return next;
      });
    }
  }, [captured, currentStep, userId, stopCamera]);

  const handleDeleteAndRecapture = useCallback(
    async (stepIndex: number) => {
      const embedding = embeddings.find(
        (e) => e.label === SLOT_LABELS[stepIndex]
      );
      if (!embedding) return;

      setError(null);
      try {
        await faceService.delete(userId, embedding.id);
        const data = await faceService.list(userId);
        setEmbeddings(data);

        // Clear image URL for deleted slot
        setImageUrls((prev) => {
          const label = SLOT_LABELS[stepIndex];
          if (prev[label]) {
            const next = { ...prev };
            delete next[label];
            return next;
          }
          return prev;
        });

        setStepStatuses((prev) => {
          const next = [...prev];
          next[stepIndex] = "pending";
          return next;
        });
        setAllComplete(false);
        setCaptured(null);
        setCurrentStep(stepIndex);
      } catch (e: unknown) {
        setError(
          e instanceof Error ? e.message : "faceEnroll.error.deleteFailed"
        );
      }
    },
    [embeddings, userId]
  );

  const goToStep = useCallback(
    (index: number) => {
      if (readonly) return;
      if (stepStatuses[index] === "done" || stepStatuses[index] === "pending") {
        stopCamera();
        setCaptured(null);
        setError(null);
        setCurrentStep(index);
      }
    },
    [readonly, stepStatuses, stopCamera]
  );

  const enrolledCount = stepStatuses.filter((s) => s === "done").length;
  const SLOT_NAMES = SLOT_NAME_KEYS.map((key) => t(key));

  return (
    <Card extra="p-6 w-full">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h4 className="text-lg font-bold text-ink">
            {t("faceEnroll.title")}
          </h4>
          <p className="text-sm text-ink-muted">
            {t("faceEnroll.subtitle", { count: MAX_SLOTS })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`text-sm font-semibold ${
              allComplete ? "text-green-500" : "text-amber-500"
            }`}
          >
            {enrolledCount}/{MAX_SLOTS}
          </span>
          {allComplete ? (
            <MdCheckCircle className="h-5 w-5 text-green-500" />
          ) : (
            <MdWarning className="h-5 w-5 text-amber-500" />
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-5 h-2 w-full rounded-full bg-gray-200 dark:bg-surface-hover">
        <div
          className={`ui-progress-fill h-2 rounded-full bg-brand-500 transition-all duration-500 ui-progress-step-${Math.min(
            enrolledCount,
            MAX_SLOTS
          )}`}
        />
      </div>

      {/* Step indicator */}
      <div className="mb-5 flex items-center justify-between gap-1">
        {SLOT_NAMES.map((name, i) => {
          const status = stepStatuses[i];
          const isCurrent = i === currentStep && !allComplete;
          return (
            <button
              key={i}
              onClick={() => goToStep(i)}
              className={`flex flex-1 flex-col items-center gap-1 rounded-lg px-1 py-2 transition-colors ${
                isCurrent
                  ? "bg-brand-50 dark:bg-brand-900/20"
                  : "hover:bg-surface-hover"
              }`}
            >
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-colors ${
                  status === "done"
                    ? "bg-green-500 text-white"
                    : status === "error"
                    ? "bg-red-500 text-white"
                    : status === "uploading"
                    ? "animate-pulse bg-amber-400 text-white"
                    : isCurrent
                    ? "bg-brand-500 text-white"
                    : "bg-gray-200 text-ink-muted dark:bg-surface-hover"
                }`}
              >
                {status === "done" ? (
                  <MdCheckCircle className="h-5 w-5" />
                ) : (
                  i + 1
                )}
              </div>
              <span
                className={`text-center text-[10px] leading-tight ${
                  isCurrent
                    ? "font-semibold text-brand-600 dark:text-brand-400"
                    : "text-ink-muted"
                }`}
              >
                {name}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-brand-500" />
        </div>
      ) : readonly ? (
        <div className="rounded-2xl bg-surface-muted p-5">
          <div className="mb-4 flex items-center gap-2 text-ink-muted">
            <MdCameraAlt className="h-5 w-5" />
            <p className="text-sm font-medium">
              {t("faceEnroll.registeredPhotos")}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {SLOT_NAMES.map((name, i) => {
              const label = SLOT_LABELS[i];
              const hasImage = Boolean(imageUrls[label]);
              return (
                <div
                  key={label}
                  className="rounded-xl border border-line bg-surface p-3"
                >
                  <p className="mb-2 text-xs font-semibold text-ink-muted">
                    {name}
                  </p>
                  <div className="h-28 w-full overflow-hidden rounded-lg border border-line bg-surface-muted">
                    {hasImage ? (
                      <img
                        src={imageUrls[label]}
                        alt={name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-xs text-ink-subtle">
                        {t("faceEnroll.notRegistered")}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : allComplete ? (
        /* All complete state — show registered faces with change option */
        <div className="rounded-2xl bg-green-50 p-5 dark:bg-green-900/10">
          <div className="mb-4 flex items-center gap-2 text-green-700 dark:text-green-400">
            <MdCheckCircle className="h-6 w-6" />
            <div>
              <p className="text-sm font-semibold">
                {t("faceEnroll.allComplete")}
              </p>
              <p className="text-xs text-green-600 dark:text-green-500">
                {canModifyExisting
                  ? t("faceEnroll.readyCanModify")
                  : t("faceEnroll.ready")}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {SLOT_NAMES.map((name, i) => (
              <div key={i} className="flex flex-col items-center gap-2">
                <div className="relative h-24 w-24 overflow-hidden rounded-xl border-2 border-green-200 bg-green-100 dark:border-green-800 dark:bg-green-900/30">
                  {imageUrls[SLOT_LABELS[i]] ? (
                    <img
                      src={imageUrls[SLOT_LABELS[i]]}
                      alt={name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <MdCheckCircle className="h-8 w-8 text-green-500" />
                    </div>
                  )}
                </div>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  {name}
                </span>
                <button
                  onClick={() => handleDeleteAndRecapture(i)}
                  disabled={!canModifyExisting}
                  className={`flex items-center gap-1 rounded-lg border border-line px-2.5 py-1 text-[11px] font-medium ${ canModifyExisting ? "text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                      : "cursor-not-allowed text-ink-subtle opacity-50"
                  }`}
                >
                  <MdCameraAlt className="h-3 w-3" />
                  {t("faceEnroll.retake")}
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* Active capture area */
        <div>
          {/* Current step instruction */}
          <div className="mb-3 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-ink">
                {t("faceEnroll.stepTitle", {
                  step: currentStep + 1,
                  name: SLOT_NAMES[currentStep],
                })}
              </p>
              <p className="text-xs text-ink-muted">
                {t(SLOT_INSTRUCTION_KEYS[currentStep])}
              </p>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => {
                  const prev = currentStep - 1;
                  if (prev >= 0) goToStep(prev);
                }}
                disabled={currentStep === 0}
                className="rounded-lg p-1.5 text-ink-subtle hover:bg-surface-hover disabled:opacity-30"
              >
                <MdNavigateBefore className="h-5 w-5" />
              </button>
              <button
                onClick={() => {
                  const next = currentStep + 1;
                  if (next < MAX_SLOTS) goToStep(next);
                }}
                disabled={currentStep === MAX_SLOTS - 1}
                className="rounded-lg p-1.5 text-ink-subtle hover:bg-surface-hover disabled:opacity-30"
              >
                <MdNavigateNext className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* If this step is already done, show completed state with re-capture option */}
          {stepStatuses[currentStep] === "done" ? (
            <div className="flex flex-col items-center gap-3 rounded-2xl bg-green-50 py-8 dark:bg-green-900/10">
              {imageUrls[SLOT_LABELS[currentStep]] ? (
                <img
                  src={imageUrls[SLOT_LABELS[currentStep]]}
                  alt={SLOT_NAMES[currentStep]}
                  className="h-32 w-32 rounded-xl object-cover shadow"
                />
              ) : (
                <MdCheckCircle className="h-12 w-12 text-green-500" />
              )}
              <p className="text-sm font-medium text-green-700 dark:text-green-400">
                {t("faceEnroll.stepDone", { name: SLOT_NAMES[currentStep] })}
              </p>
              <button
                onClick={() => handleDeleteAndRecapture(currentStep)}
                disabled={!canModifyExisting}
                className={`flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-xs font-medium ${ canModifyExisting ? "text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                    : "cursor-not-allowed text-ink-subtle opacity-50"
                }`}
              >
                <MdDelete className="h-3.5 w-3.5" />
                {t("faceEnroll.retake")}
              </button>
            </div>
          ) : (
            /* Camera viewfinder */
            <div className="bg-black overflow-hidden rounded-2xl border-2 border-line">
              <div className="ui-aspect-4-3 relative">
                {cameraError ? (
                  <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
                    <MdWarning className="h-10 w-10 text-red-400" />
                    <p className="text-sm text-red-400">{t(cameraError)}</p>
                    <button
                      onClick={startCamera}
                      className="flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-xs text-white hover:bg-white/20"
                    >
                      <MdRefresh className="h-4 w-4" />
                      {t("action.retry")}
                    </button>
                  </div>
                ) : stepStatuses[currentStep] === "uploading" ? (
                  <div className="flex h-full flex-col items-center justify-center gap-3">
                    <div className="border-t-transparent h-10 w-10 animate-spin rounded-full border-4 border-brand-500" />
                    <p className="text-sm text-white/70">
                      {t("faceEnroll.uploading")}
                    </p>
                  </div>
                ) : captured ? (
                  <img
                    src={captured}
                    alt="captured"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="h-full w-full object-cover"
                    />
                    {/* Oval guide overlay */}
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <div className="h-48 w-36 rounded-full border-2 border-dashed border-white/50" />
                    </div>
                  </>
                )}
              </div>

              <canvas ref={canvasRef} className="hidden" />

              {/* Camera controls */}
              <div className="flex items-center justify-center gap-3 bg-gray-900 px-5 py-4">
                {stepStatuses[currentStep] === "captured" && captured ? (
                  <>
                    <button
                      onClick={retakePhoto}
                      className="flex-1 rounded-xl border border-white/20 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/10"
                    >
                      {t("faceEnroll.retake")}
                    </button>
                    <button
                      onClick={confirmAndUpload}
                      className="flex-1 rounded-xl bg-brand-500 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-600"
                    >
                      {t("faceEnroll.confirmUpload")}
                    </button>
                  </>
                ) : stepStatuses[currentStep] === "error" ? (
                  <>
                    <button
                      onClick={retakePhoto}
                      className="flex-1 rounded-xl border border-white/20 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/10"
                    >
                      {t("faceEnroll.retake")}
                    </button>
                  </>
                ) : stepStatuses[currentStep] === "uploading" ? (
                  <p className="text-sm text-white/50">
                    {t("faceEnroll.processing")}
                  </p>
                ) : !cameraError ? (
                  <button
                    onClick={capturePhoto}
                    className="h-14 w-14 rounded-full border-4 border-brand-500 bg-white shadow-md transition-transform hover:scale-105 active:scale-95"
                  >
                    <MdCameraAlt className="mx-auto h-6 w-6 text-brand-500" />
                  </button>
                ) : null}
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mt-3 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
              {t(error)}
            </div>
          )}
        </div>
      )}

      {/* Footer hint */}
      {!loading && !allComplete && !readonly && (
        <p className="mt-4 text-xs text-ink-subtle">
          {t("faceEnroll.remainingHint", { count: MAX_SLOTS - enrolledCount })}
        </p>
      )}
    </Card>
  );
}
